import type { ClubRequestDto, ClubSummaryDto } from '@rw/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { Button, Card, SectionTitle } from '../../components/ui';
import { useClubSearch, useMyClub } from '../../hooks/queries';
import { formatKm, formatNumber } from '../../lib/format';
import { ClubBadge } from './ClubBadge';
import { ClubFormSheet } from './ClubFormSheet';
import { clubApi, useClubAction } from './useClubActions';
import { useT } from '../../i18n';

export function ClubsPage() {
  const my = useMyClub();
  const [q, setQ] = useState('');
  const search = useClubSearch(q);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const t = useT();

  const accept = useClubAction((id: string) => clubApi.acceptInvite(id), 'Welcome to the club!');
  const declineInv = useClubAction((id: string) => clubApi.declineInvite(id));
  const cancel = useClubAction((id: string) => clubApi.cancelRequest(id), 'Request cancelled');
  const request = useClubAction((id: string) => clubApi.requestJoin(id), 'Request sent — the owner will review it');

  const m = my.data;
  const pendingFor = new Set(m?.requests.map((r) => r.club.id));
  // Your own club is shown above; don't list it again in the search results.
  const results = search.data?.items.filter((c) => c.id !== m?.club?.id) ?? [];

  return (
    <Page>
      <h1 className="font-display text-4xl font-extrabold uppercase">{t('Clubs')}</h1>
      <p className="mt-1 text-sm text-ink-400">{t('Run together. Every sector your members hold is club territory.')}</p>

      {my.isPending ? (
        <LoadingState />
      ) : my.isError && !m ? (
        <ErrorState error={my.error} onRetry={() => my.refetch()} />
      ) : m ? (
        <>
          {m.club ? (
            <>
              <SectionTitle>{t('Your club')}</SectionTitle>
              <ClubRow club={m.club} badge={m.role === 'OWNER' && m.pendingToReview ? t('{n} to review', { n: m.pendingToReview }) : undefined} testId="my-club" />
            </>
          ) : (
            <Card className="mt-4 text-center">
              <div className="font-display text-xl font-bold uppercase">{t('You’re running solo')}</div>
              <p className="mt-1 text-sm text-ink-400">{t('Join a club below, or start your own crew.')}</p>
              <Button className="mt-4 w-full" onClick={() => setCreating(true)} data-testid="create-club">
                {t('Create a club')}
              </Button>
            </Card>
          )}

          {m.invites.length ? (
            <>
              <SectionTitle>{t('Invitations')}</SectionTitle>
              <div className="space-y-2">
                {m.invites.map((i) => (
                  <InviteRow
                    key={i.id}
                    r={i}
                    onAccept={() => accept.mutate(i.id)}
                    onDecline={() => declineInv.mutate(i.id)}
                    busy={accept.isPending || declineInv.isPending}
                    disabled={!!m.club}
                  />
                ))}
              </div>
            </>
          ) : null}

          {m.requests.length ? (
            <>
              <SectionTitle>{t('Waiting for approval')}</SectionTitle>
              <div className="space-y-2">
                {m.requests.map((r) => (
                  <Card key={r.id} className="flex items-center gap-3">
                    <ClubBadge club={r.club} size="sm" />
                    <div className="flex-1 font-semibold">{r.club.name}</div>
                    <Button size="sm" variant="ghost" onClick={() => cancel.mutate(r.id)}>
                      {t('Cancel')}
                    </Button>
                  </Card>
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : null}

      <SectionTitle>{t('Find a club')}</SectionTitle>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('Search clubs by name')}
        className="h-12 w-full rounded-xl border border-ink-700 bg-ink-850 px-4 outline-none focus:border-volt"
        data-testid="club-search"
        aria-label={t('Search clubs')}
      />
      <div className="mt-3 space-y-2">
        {search.isPending ? (
          <LoadingState />
        ) : results.length === 0 ? (
          <EmptyState icon="⬢" title={t('No clubs found')} body={t(q ? 'Try another name, or create it yourself.' : 'Be the first to start a club in Đà Nẵng.')} />
        ) : (
          results.map((c) => (
            <ClubRow
              key={c.id}
              club={c}
              action={
                m && !m.club ? (
                  pendingFor.has(c.id) ? (
                    <span className="text-xs font-semibold text-ink-400">{t('Requested')}</span>
                  ) : (
                    <Button size="sm" onClick={(e) => (e.preventDefault(), request.mutate(c.id))} data-testid={`join-${c.name}`}>
                      {t('Join')}
                    </Button>
                  )
                ) : undefined
              }
            />
          ))
        )}
      </div>

      <ClubFormSheet open={creating} onClose={() => setCreating(false)} onSaved={(id) => navigate(`/clubs/${id}`)} />
    </Page>
  );
}

export function ClubRow({ club, action, badge, testId }: { club: ClubSummaryDto; action?: React.ReactNode; badge?: string; testId?: string }) {
  const t = useT();
  return (
    <Link to={`/clubs/${club.id}`} className="flex items-center gap-3 rounded-2xl border border-ink-700/80 bg-ink-850 p-3 active:bg-ink-800" data-testid={testId}>
      <ClubBadge club={club} link={false} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-lg font-bold">{club.name}</div>
        <div className="text-xs text-ink-400">
          {t(club.stats.members === 1 ? '{n} member' : '{n} members', { n: club.stats.members })} · ⬢ {formatNumber(club.stats.territories)} · {formatKm(club.stats.totalDistanceM, 0)} km
        </div>
        {badge ? <div className="mt-0.5 text-xs font-semibold text-gold">{badge}</div> : null}
      </div>
      {action}
    </Link>
  );
}

function InviteRow({ r, onAccept, onDecline, busy, disabled }: { r: ClubRequestDto; onAccept: () => void; onDecline: () => void; busy: boolean; disabled: boolean }) {
  const t = useT();
  return (
    <Card className="flex items-center gap-3" >
      <ClubBadge club={r.club} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{r.club.name}</div>
        <div className="text-xs text-ink-400">{r.invitedBy ? t('Invited by {name}', { name: r.invitedBy.displayName }) : t('Invitation')}{disabled ? ` · ${t('leave your club to accept')}` : ''}</div>
      </div>
      <Button size="sm" onClick={onAccept} disabled={busy || disabled} data-testid="accept-invite">
        {t('Join')}
      </Button>
      <Button size="sm" variant="ghost" onClick={onDecline} disabled={busy}>
        ✕
      </Button>
    </Card>
  );
}
